import { useState } from 'react';
import { signInWithEmailAndPassword, signInWithPopup, linkWithCredential, GoogleAuthProvider, sendPasswordResetEmail } from 'firebase/auth';
import { auth, googleProvider } from '../firebase/config';
import { Link, useNavigate } from 'react-router-dom';
import Modal from '../components/Modal';

function EmailIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 5L2 7" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 8 10 8a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.5 13.5 0 0 0 2 12s3.5 8 10 8a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Account linking state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkPassword, setLinkPassword] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [pendingCredential, setPendingCredential] = useState(null);
  const [pendingEmail, setPendingEmail] = useState('');

  // Forgot password state
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotError, setForgotError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      navigate('/');
    } catch (err) {
      if (err.code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please try again.');
      } else if (err.code === 'auth/user-not-found') {
        setError('No account found with this email. Please sign up first.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed login attempts. Please try again later or reset your password.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Network error. Please check your internet connection.');
      } else {
        setError('Failed to log in. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    setLoading(true);

    try {
      await signInWithPopup(auth, googleProvider);
      navigate('/');
    } catch (err) {
      if (err.code === 'auth/account-exists-with-different-credential') {
        const email = err.customData?.email;
        const credential = GoogleAuthProvider.credentialFromError(err);
        setPendingCredential(credential);
        setPendingEmail(email);
        setShowLinkModal(true);
      } else if (err.code !== 'auth/popup-closed-by-user') {
        setError(err.message || 'Failed to sign in with Google.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLinkGoogle = async (e) => {
    e.preventDefault();
    setLinkError(null);
    setLinkLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, pendingEmail, linkPassword);
      await linkWithCredential(userCredential.user, pendingCredential);
      navigate('/');
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setLinkError('Incorrect password. Please try again.');
      } else {
        setLinkError('Failed to link accounts. Please try again.');
      }
    } finally {
      setLinkLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotMsg('');
    setForgotLoading(true);

    try {
      await sendPasswordResetEmail(auth, forgotEmail.trim());
      setForgotMsg('Password reset email sent. Check your inbox.');
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setForgotError('No account found with this email.');
      } else {
        setForgotError('Failed to send reset email. Please try again.');
      }
    } finally {
      setForgotLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) setError(null);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (error) setError(null);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <a
          href="https://www.sohera.co.za/"
          className="sohera-back"
          style={{ marginBottom: '1.25rem' }}
        >
          ← Back to SoHera
        </a>

        <div className="auth-logo">
          <img src="/logo-dark.png" alt="SoHera_" className="auth-logo-img" />
          <p className="auth-logo-subtitle">Invoice System · Sign in to manage your invoices</p>
        </div>

        {error && (
          <div className="auth-error" role="alert">
            <AlertIcon />
            <span>{error}</span>
          </div>
        )}

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="auth-input-group">
            <label htmlFor="email-address" className="auth-label">Email address</label>
            <div className="auth-input-wrap">
              <EmailIcon />
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="auth-input"
                placeholder="you@example.com"
                value={email}
                onChange={handleEmailChange}
              />
            </div>
          </div>

          <div className="auth-input-group">
            <label htmlFor="password" className="auth-label">Password</label>
            <div className="auth-input-wrap">
              <LockIcon />
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                className="auth-input auth-input--password"
                placeholder="Enter your password"
                value={password}
                onChange={handlePasswordChange}
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

          <div className="auth-forgot">
            <button
              type="button"
              className="auth-link"
              onClick={() => {
                setForgotEmail(email);
                setShowForgotModal(true);
                setForgotMsg('');
                setForgotError('');
              }}
            >
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="auth-button"
          >
            {loading ? (
              <>
                <span className="auth-button-spinner" aria-hidden="true" />
                Signing in...
              </>
            ) : (
              'Sign in'
            )}
          </button>

          <div className="auth-divider">
            <span>or</span>
          </div>

          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={loading}
            className="auth-google-btn"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {loading ? 'Signing in...' : 'Continue with Google'}
          </button>

          <div className="auth-footer">
            <p>
              Don't have an account?{' '}
              <Link to="/signup" className="auth-link">
                Sign up
              </Link>
            </p>
          </div>
        </form>
      </div>

      <Modal
        isOpen={showLinkModal}
        onClose={() => {
          setShowLinkModal(false);
          setLinkPassword('');
          setLinkError(null);
        }}
        title="Connect Google Account"
      >
        <p style={{ fontSize: '0.9rem', color: '#4b5563', marginBottom: '0.75rem' }}>
          An account with <strong>{pendingEmail}</strong> already exists.
        </p>
        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>
          Enter your password to connect your Google account to it.
        </p>

        {linkError && (
          <div className="auth-error" style={{ marginBottom: '1rem' }}>
            <AlertIcon />
            <span>{linkError}</span>
          </div>
        )}

        <form onSubmit={handleLinkGoogle}>
          <div className="auth-input-group">
            <label htmlFor="link-password" className="auth-label">Password</label>
            <div className="auth-input-wrap">
              <LockIcon />
              <input
                id="link-password"
                type="password"
                className="auth-input"
                placeholder="Enter your existing password"
                value={linkPassword}
                onChange={(e) => {
                  setLinkPassword(e.target.value);
                  if (linkError) setLinkError(null);
                }}
                required
                autoFocus
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={linkLoading}
            className="auth-button"
            style={{ marginTop: '0.5rem' }}
          >
            {linkLoading ? (
              <>
                <span className="auth-button-spinner" aria-hidden="true" />
                Connecting...
              </>
            ) : (
              'Connect & Sign In'
            )}
          </button>
        </form>
      </Modal>

      {/* Forgot Password Modal */}
      <Modal
        isOpen={showForgotModal}
        onClose={() => {
          setShowForgotModal(false);
          setForgotEmail('');
          setForgotMsg('');
          setForgotError('');
        }}
        title="Reset Password"
      >
        {forgotMsg ? (
          <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>&#9993;</div>
            <p style={{ fontSize: '0.9rem', color: '#4b5563', marginBottom: '0.5rem' }}>{forgotMsg}</p>
            <p style={{ fontSize: '0.82rem', color: '#9ca3af' }}>Check your spam folder if you don't see it.</p>
          </div>
        ) : (
          <>
            <p style={{ fontSize: '0.9rem', color: '#4b5563', marginBottom: '1rem' }}>
              Enter your email and we'll send you a link to reset your password.
            </p>

            {forgotError && (
              <div className="auth-error" style={{ marginBottom: '1rem' }}>
                <AlertIcon />
                <span>{forgotError}</span>
              </div>
            )}

            <form onSubmit={handleForgotPassword}>
              <div className="auth-input-group">
                <label htmlFor="forgot-email" className="auth-label">Email address</label>
                <div className="auth-input-wrap">
                  <EmailIcon />
                  <input
                    id="forgot-email"
                    type="email"
                    className="auth-input"
                    placeholder="you@example.com"
                    value={forgotEmail}
                    onChange={(e) => { setForgotEmail(e.target.value); setForgotError(''); }}
                    required
                    autoFocus
                  />
                </div>
              </div>
              <button type="submit" disabled={forgotLoading} className="auth-button" style={{ marginTop: '0.25rem' }}>
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          </>
        )}
      </Modal>
    </div>
  );
}
